// Native shell mode bridge — talks to the RoavaShell Capacitor plugin (iOS).
//
// Cold-launch behavior is owned by Swift (RoavaBridgeViewController +
// RemoteFirstLoader): the bundled `public/index.html` paints first; the native
// loader probes https://roava.lovable.app in the background and swaps the
// WebView to remote when reachable. This module just lets JS observe the
// resulting mode, request a manual retry, read the last shell error, and
// read process-level device flags (e.g. Low Power Mode) that aren't exposed
// by @capacitor/device.
//
// On web (and on iOS pre-v5.6 binaries that don't have RoavaShell), every
// call returns a safe default so consumers never have to special-case.

import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

export type ShellMode = "remote" | "bundled";

/** Fixed enum of shell-error reasons. Native side mirrors these strings. */
export type ShellErrorReason =
  | "probe_timeout"
  | "probe_failed"
  | "nav_provisional"
  | "nav_committed"
  | "safety_timeout"
  | "bundled_fallback"
  | "remote_success_cleared"
  | "unknown";

export interface ShellError {
  reason: ShellErrorReason;
  detail?: string | null;
  occurredAt: string;
  resolved: boolean;
}

export type ShellModeReason =
  | "boot"
  | "boot-pre-remote"
  | "local-first"
  | "local-only"
  | "remote-enabled"
  | "online-remote"
  | "remote-probe-failed"
  | "remote-navigation-failed"
  | "debug-override"
  | "local-failed"
  | "manual-retry"
  | "remote_success_cleared"
  | string;

export type ShellConnectionType = "wifi" | "cellular" | "wired" | "other" | "none" | "unknown";

export interface ShellModeSnapshot {
  mode: ShellMode;
  lastSwitchAt: string | null;
  /** Most recent unresolved shell error, or null when the shell is healthy. */
  lastError: ShellError | null;
  /** Why the current mode was selected. Surfaced for admin diagnostics. */
  modeReason: ShellModeReason;
  /** Current link type reported by NWPathMonitor. */
  connectionType: ShellConnectionType;
}

export interface ShellConfig {
  remoteUrl: string;
  remoteTimeoutMs: number;
  reconnectDebounceMs: number;
  /** "always" | "on-failure" | "never". Default "on-failure". */
  remoteMode: string;
  /** Whether the diagnostic probe runs in local-first/local-only modes. */
  remoteProbeDiagnostic: boolean;
}

export interface ShellDeviceFlags {
  /** ProcessInfo.processInfo.isLowPowerModeEnabled on iOS. null on web. */
  lowPowerMode: boolean | null;
}

interface RoavaShellPlugin {
  getMode(): Promise<ShellModeSnapshot>;
  reloadFromRemote(): Promise<{ ok: boolean; mode: ShellMode }>;
  getConfig(): Promise<ShellConfig>;
  getLastError(): Promise<{ lastError: ShellError | null }>;
  getDeviceFlags(): Promise<{ lowPowerMode: boolean; isLowPowerModeEnabled: boolean }>;
  setRemoteOverride(opts: { enabled: boolean }): Promise<{ enabled: boolean; key: string }>;
  addListener(
    eventName: "shellModeChange",
    listener: (snapshot: ShellModeSnapshot) => void,
  ): Promise<PluginListenerHandle>;
}

const RoavaShell = registerPlugin<RoavaShellPlugin>("RoavaShell");

function isAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("RoavaShell");
}

const WEB_DEFAULT: ShellModeSnapshot = {
  mode: "remote",
  lastSwitchAt: null,
  lastError: null,
  modeReason: "remote-enabled",
  connectionType: "unknown",
};

function normalizeConnection(raw: any): ShellConnectionType {
  const v = typeof raw === "string" ? raw : "unknown";
  return (["wifi", "cellular", "wired", "other", "none", "unknown"] as const).includes(v as any)
    ? (v as ShellConnectionType)
    : "unknown";
}

function normalizeSnapshot(raw: any): ShellModeSnapshot {
  return {
    mode: raw?.mode === "remote" ? "remote" : "bundled",
    lastSwitchAt: raw?.lastSwitchAt ?? null,
    lastError: normalizeError(raw?.lastError),
    modeReason: typeof raw?.modeReason === "string" ? raw.modeReason : "boot",
    connectionType: normalizeConnection(raw?.connectionType),
  };
}

const KNOWN_REASONS: ReadonlySet<ShellErrorReason> = new Set([
  "probe_timeout",
  "probe_failed",
  "nav_provisional",
  "nav_committed",
  "safety_timeout",
  "bundled_fallback",
  "remote_success_cleared",
  "unknown",
]);

function normalizeError(raw: any): ShellError | null {
  if (!raw || typeof raw !== "object") return null;
  const reason = KNOWN_REASONS.has(raw.reason) ? (raw.reason as ShellErrorReason) : "unknown";
  return {
    reason,
    detail: typeof raw.detail === "string" ? raw.detail : null,
    occurredAt: typeof raw.occurredAt === "string" ? raw.occurredAt : new Date().toISOString(),
    resolved: raw.resolved === true,
  };
}

/** Current native shell mode + last error. Returns `remote`/null on web. */
export async function getShellMode(): Promise<ShellModeSnapshot> {
  if (!isAvailable()) return WEB_DEFAULT;
  try {
    const raw = await RoavaShell.getMode();
    return normalizeSnapshot(raw);
  } catch {
    return WEB_DEFAULT;
  }
}

/** Manually retry the remote load. No-op (returns ok:true) on web. */
export async function reloadShellFromRemote(): Promise<{ ok: boolean; mode: ShellMode }> {
  if (!isAvailable()) return { ok: true, mode: "remote" };
  try {
    return await RoavaShell.reloadFromRemote();
  } catch {
    return { ok: false, mode: "bundled" };
  }
}

/** Subscribe to mode flips. Returns an unsubscribe function. No-op on web. */
export function onShellModeChange(
  cb: (snapshot: ShellModeSnapshot) => void,
): () => void {
  if (!isAvailable()) return () => {};
  let handle: PluginListenerHandle | null = null;
  let cancelled = false;
  void RoavaShell.addListener("shellModeChange", (raw) => {
    cb(normalizeSnapshot(raw));
  }).then((h) => {
    if (cancelled) {
      void h.remove();
      return;
    }
    handle = h;
  });
  return () => {
    cancelled = true;
    if (handle) void handle.remove();
  };
}

/** Native shell config (timeouts, remote URL). Throws-free on web. */
export async function getShellConfig(): Promise<ShellConfig | null> {
  if (!isAvailable()) return null;
  try {
    return await RoavaShell.getConfig();
  } catch {
    return null;
  }
}

/** Last unresolved shell error, or null when healthy. Null on web. */
export async function getShellLastError(): Promise<ShellError | null> {
  if (!isAvailable()) return null;
  try {
    const res = await RoavaShell.getLastError();
    return normalizeError(res?.lastError);
  } catch {
    return null;
  }
}

/**
 * Process-level device flags that @capacitor/device does not expose
 * (notably Low Power Mode). Returns { lowPowerMode: null } on web or on
 * pre-v5.6 binaries that don't have the native method registered.
 */
export async function getShellDeviceFlags(): Promise<ShellDeviceFlags> {
  if (!isAvailable()) return { lowPowerMode: null };
  try {
    const res = await RoavaShell.getDeviceFlags();
    if (typeof res?.lowPowerMode === "boolean") {
      return { lowPowerMode: res.lowPowerMode };
    }
    return { lowPowerMode: null };
  } catch {
    return { lowPowerMode: null };
  }
}

/**
 * Toggle the debug/admin "force remote" override. Stored in iOS UserDefaults
 * (key `RoavaShellForceRemote`) and read at next cold launch by
 * `RoavaShellPlugin.resolveShellPolicy()`. No-op on web.
 */
export async function setShellRemoteOverride(enabled: boolean): Promise<boolean> {
  if (!isAvailable()) return false;
  try {
    const res = await RoavaShell.setRemoteOverride({ enabled });
    return res.enabled === true;
  } catch {
    return false;
  }
}
